import { Card, Table, Radio, Button, Modal, Form, Select } from 'antd';
import { sortBy, unionBy, keyBy, cloneDeep, forEach, isNil, round, find, groupBy, trim, difference } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import APIList from '../../../../http/ApiList';
import { FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID } from '../../../../enum/common.enum';
import { componentDefinition } from '../../component-definition';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import { camelToSnake, kpiValue, toCamelCase } from '../../../../utils/farecast-util';
import { ExclamationCircleTwoTone, MinusCircleOutlined, PlusOutlined, RollbackOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelectorSize } from '../../../../hooks/useWindowSize';
import { getS3Object } from '@meteor/frontend-core';
import { onDataCellHandler, onTitleCellHandler } from '../../../../utils/handler';
import { useNavigate } from 'react-router-dom';
import { useLabel } from '../../../../hooks/useLabel';

const ForecastTable: React.FC<any> = (props) => {
  const navigate = useNavigate();
  const { setAllComponents, event$, color, tralitSettings } = useContext(AppContext);
  const { getCompareLabel } = useLabel();
  const { property } = props;
  const [form] = Form.useForm();
  const instance = useRef(null);
  const [selectedKpis, setSelectedKpis] = useState<any[]>();
  // 过滤条件
  const [filter, setFilter] = useState<any>();
  // 完整数据
  const [dataProvider, setDataProvider] = useState<any>({
    current: {},
    target: {},
    alert: [],
    computed: { current: {}, target: {} },
  });
  const { current: currentDataAll, target: targetDataAll, computed, alert } = dataProvider;
  // 展开KPI Key
  const [expandedRowKeys, setExpandedRowKeys] = useState<any[]>([]);
  // KPI Fields
  const [kpiFields, setKpiFields] = useState([]);
  // Drill Down Fields
  const [drillFields, setDrillFields] = useState([]);
  // 获取Data
  const current = useMemo(() => {
    return currentDataAll.common;
  }, [currentDataAll]);
  // 获取Plan
  const target = useMemo(() => {
    return targetDataAll.common;
  }, [targetDataAll]);
  // 保存的Columns设置
  const [drillSaveFields, setDrillSaveFields] = useState<any[]>();
  // 根据drillFields和数据整理出来的drillData
  const drillData = useMemo(() => {
    if (drillSaveFields) {
      return drillSaveFields.map((field) => {
        const values = unionBy(current, toCamelCase(field.fieldKey)).map((item) => item[toCamelCase(field.fieldKey)]);
        return {
          ...field,
          fieldKey: toCamelCase(field.fieldKey),
          values,
        };
      });
    }
    return sortBy(
      drillFields
        .filter((drillField) => drillField.important > FIELD_IMPORTANT.LEVEL0)
        .map((field) => {
          const values = unionBy(current, field.fieldKey).map((item) => item[field.fieldKey]);
          return {
            ...field,
            values,
          };
        }),
      'priority'
    );
  }, [drillFields, drillSaveFields, current]);
  // 根据alert和drillData，kpiFields整理出来的alertData
  const alertData = useMemo(() => {
    return alert.map((alertItem) => {
      const kpiKeys = kpiFields.reduce((acc, cur) => {
        if (alertItem[cur.fieldKey]) {
          acc.push(cur.fieldKey);
        }
        return acc;
      }, []);
      const drillFieldsFinal = drillFields.reduce((acc, drillField) => {
        if (drillField.isMenu && drillField.children && drillField.children.length > 0) {
          drillField.children.forEach((child) => {
            acc.push(child);
          });
        } else {
          acc.push(drillField);
        }
        return acc;
      }, []);
      const drills = drillFieldsFinal.reduce((acc, cur) => {
        if (alertItem[cur.fieldKey]) {
          acc.push({
            drillKey: cur.fieldKey,
            drillValue: alertItem[cur.fieldKey],
          });
        }
        return acc;
      }, []);
      return {
        kpiKeys,
        drills,
      };
    });
  }, [alert, drillFields, kpiFields]);
  // 获取Table滚动条高度
  const { selectorHeight4Table } = useSelectorSize({
    watcherSelector: ['.tralit-table-header', '.chart-group-container', '.tralit-table-tbody'],
  });
  // Loading
  const [tableLoading, setTableLoading] = useState(true);
  // highLigh Drill Fields
  const [selectedDrillData, setSelectedDrillData] = useState<any[]>();

  // Table Columns
  const columns = useMemo(() => {
    // 获取文字颜色
    const getColor = (key, radio, isHighlight = false) => {
      // const reverse = kpiUnitPrecisionMap[key].reverse;
      // if (reverse) {
      //   if (radio > 1.2) {
      //     return color.errorColor;
      //   }
      //   if (radio > 1) {
      //     return isHighlight ? '#F6E300' : color.warningColor;
      //   }
      //   return color.successColor;
      // } else {
      if (radio >= 1) {
        return color.successColor;
      }
      if (radio >= 0.8) {
        return isHighlight ? '#F6E300' : color.warningColor;
      }
      return color.errorColor;
      // }
    };

    // 值类型单元格渲染
    const valueRender = (nodeKey) => (text, record) => {
      if (isNil(text)) {
        return '-';
      }
      const fieldNode = kpiFields.find((field) => field.fieldKey === record.fieldKey);
      const showText = kpiValue(text, fieldNode.unitValue);
      return (
        <>
          <span
            className={`${
              selectedKpis?.includes(camelToSnake(record.fieldKey)) ||
              (selectedDrillData.length > 0 &&
                selectedDrillData?.every(({ fieldKey, values }) => {
                  const groups = nodeKey.split('&');
                  return groups.some((group) => {
                    const [key, value] = group.split(':');
                    return key === toCamelCase(fieldKey) && values.includes(value);
                  });
                }))
                ? 'highLight'
                : ''
            } `}
          >
            {showText}
          </span>
        </>
      );
    };

    // 非值类型单元格渲染
    const highlighterRender = (nodeKey) => (text, record) => {
      const current = record[`${nodeKey}@current`];
      const target = record[`${nodeKey}@target`];
      const radio = isNil(target) ? null : target ? current / target : 0;
      const diff = isNil(target) ? null : current - target;
      const fieldNode = kpiFields.find((field) => field.fieldKey === record.fieldKey);
      if (isNil(target)) {
        return <div>-</div>;
      }

      const conditions = nodeKey.split('&').map((dataIndex) => {
        const [drillKey, drillValue] = dataIndex.split(':');
        return { drillKey, drillValue };
      });

      const isAlert = alertData.some((alertCondition) => {
        const { kpiKeys, drills } = alertCondition;
        return (
          drills.every((drill) =>
            conditions.some((condition) => condition.drillKey === drill.drillKey && condition.drillValue === drill.drillValue)
          ) && kpiKeys.some((kpiKey) => record.fieldKey === kpiKey)
        );
      });

      const showText = filter.switchValue ? (
        <>
          {round(radio * 100, 1) || 0}
          <span className="percent-label">%</span>
        </>
      ) : (
        kpiValue(diff, fieldNode.unitValue, true)
      );
      return (
        <>
          <div className={`${isAlert ? 'alert' : ''} ${selectedKpis?.includes(camelToSnake(record.fieldKey)) ? 'highLight' : ''}`}>
            <span>
              <span style={{ color: getColor(record.key, radio) }}>{showText}</span>
            </span>
          </div>
        </>
      );
    };

    // Header Title & Alert
    const getAlertHeaderTitle = (title, nodeKey) => {
      const conditions = nodeKey.split('&').map((dataIndex) => {
        const [drillKey, drillValue] = dataIndex.split(':');
        return { drillKey, drillValue };
      });
      const nodeCondition = conditions[conditions.length - 1];

      const isHighlight = alertData.some((conditionAlert) => {
        const drills = conditionAlert.drills;
        if (drills && drills.length > 0) {
          return (
            // 筛选每一个alert的drillKey，在当前列中都存在，并且值符合
            drills.every(({ drillKey, drillValue }) => {
              // const drillValues = gbDrillKey[drillKey].map((item) => item.drillValue);
              const findCondition = conditions.find((condition) => condition.drillKey === drillKey);
              return findCondition && findCondition.drillKey === drillKey && drillValue === findCondition.drillValue;
            }) &&
            // 筛选当前列层级的key符合
            drills.map((drill) => drill.drillKey).includes(nodeCondition.drillKey)
          );
        }
      });

      const canRedirect = property.canRedirect;

      return (
        <>
          {canRedirect ? (
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => {
                const params = conditions.reduce((acc, cur) => {
                  const { drillKey, drillValue } = cur;
                  acc[camelToSnake(drillKey)] = drillValue;
                  return acc;
                }, {});
                const paramsStr = new URLSearchParams(Object.entries(params)).toString();
                navigate(`/tralit/compare?${paramsStr}&compareDrillKey=${camelToSnake(nodeCondition.drillKey)}`);
              }}
            >
              {title}
            </span>
          ) : (
            title
          )}
          {isHighlight && <ExclamationCircleTwoTone style={{ marginLeft: 6, fontSize: 16 }} twoToneColor={color.errorColor} />}
        </>
      );
    };

    // KPI ツリー 单元格渲染
    const alertTitleRender = (text, record) => {
      const curRowKey = record.fieldKey;
      const isHighlight = alertData.some((conditionAlert) => conditionAlert.kpiKeys.includes(curRowKey));
      return (
        <span className={selectedKpis?.includes(camelToSnake(curRowKey)) ? 'highLight' : ''}>
          {text}
          {isHighlight && <ExclamationCircleTwoTone style={{ marginLeft: 6, fontSize: 16 }} twoToneColor={color.errorColor} />}
        </span>
      );
    };

    // 递归获取Columns
    const getColumns = (index = 0, path = []) => {
      if (index > drillData.length) {
        return null;
      }
      const currentLevel = drillData[index];
      const kpiTree = [];
      const allColumns = [];
      if (index === 0) {
        kpiTree.push({
          key: `kpiTree`,
          dataIndex: `kpiTree`,
          title: <>KPI ツリー</>,
          width: 250,
          render: alertTitleRender,
          onCell: onTitleCellHandler,
        });
        allColumns.push({
          key: `all`,
          title: 'All',
          children: [
            {
              key: `all@current`,
              dataIndex: `all@current`,
              title: `値`,
              className: 'text-right',
              render: valueRender('all'),
              width: 110,
              onCell: onDataCellHandler,
            },
            {
              key: `all@target`,
              dataIndex: `all@target`,
              title: getCompareLabel(filter),
              className: 'text-right',
              render: highlighterRender('all'),
              width: 80,
              onCell: onDataCellHandler,
            },
          ],
        });
      }
      if (!currentLevel) {
        const nodeKey = path.join('&');
        return [
          {
            key: `${nodeKey}@current`,
            dataIndex: `${nodeKey}@current`,
            title: `値`,
            className: 'text-right',
            render: valueRender(nodeKey),
            width: 110,
            onCell: onDataCellHandler,
          },
          {
            key: `${nodeKey}@target`,
            dataIndex: `${nodeKey}@target`,
            title: getCompareLabel(filter),
            className: 'text-right',
            render: highlighterRender(nodeKey),
            width: 80,
            onCell: onDataCellHandler,
          },
        ];
      }
      return [
        ...kpiTree,
        ...allColumns,
        ...currentLevel.values
          .filter((curValue) => {
            const curParentKeys = currentLevel.parentKey?.split(',');
            if (curParentKeys && curParentKeys.length > 0) {
              const parentLevel = drillData.find((drillField) => curParentKeys.includes(drillField.fieldKey));
              if (parentLevel) {
                return current.some(
                  (item) =>
                    path.some((kv) => {
                      const [key, value] = kv.split(':');
                      if (key === parentLevel.fieldKey) {
                        return item[parentLevel.fieldKey] === value;
                      }
                      return false;
                    }) && item[currentLevel.fieldKey] === curValue
                );
              }
              return true;
            }
            return true;
          })
          .map((value) => {
            const newPath = path.concat([`${currentLevel.fieldKey}:${value}`]);
            const nodeKey = newPath.join('&');
            return {
              key: nodeKey,
              dataIndex: nodeKey,
              title: getAlertHeaderTitle(value, nodeKey),
              className: 'text-right',
              children: getColumns(index + 1, newPath),
            };
          }),
      ];
    };

    const columns = getColumns();
    return columns;
  }, [drillData, filter, alertData, property, selectedKpis, selectedDrillData]);

  // Table 数据
  const dataSource = useMemo(() => {
    const setData = (node) => {
      node.kpiTree = `${node.label}${node.unitName ? `(${node.unitName})` : ''}`;
      const setNodeData = (columns) => {
        columns.forEach((column) => {
          if (column.children && column.children.length > 0) {
            setNodeData(column.children);
          } else {
            const [conditionGroup, type] = column.dataIndex.split('@');
            const conditions = conditionGroup.split('&').map((dataIndex) => {
              const [drillKey, drillValue] = dataIndex.split(':');
              return { drillKey, drillValue };
            });
            if (type === 'current') {
              if (conditionGroup === 'all') {
                const { current: currentDataTotal } = computed;
                node[column.dataIndex] = currentDataTotal ? currentDataTotal[node.fieldKey] : null;
              } else {
                const filtered = current.find((item) => conditions.every((condition) => item[condition.drillKey] === condition.drillValue));
                node[column.dataIndex] = filtered ? filtered[node.fieldKey] : null;
              }
            }
            if (type === 'target') {
              if (conditionGroup === 'all') {
                const { target: targetDataTotal } = computed;
                node[column.dataIndex] = targetDataTotal ? targetDataTotal[node.fieldKey] : null;
              } else {
                const filtered = target.find((item) => conditions.every((condition) => item[condition.drillKey] === condition.drillValue));
                node[column.dataIndex] = filtered ? filtered[node.fieldKey] : null;
              }
            }
          }
        });
      };
      setNodeData(columns.filter((column) => column.dataIndex !== 'kpiTree'));
    };
    const buildFieldTree = () => {
      const map = keyBy(cloneDeep(kpiFields), 'fieldKey');
      const result = [];
      forEach(map, (node) => {
        if (node.parentKey && map[node.parentKey]) {
          map[node.parentKey].children = map[node.parentKey].children || [];
          map[node.parentKey].children.push(node);
        } else if (!node.parentKey) {
          result.push(node);
        }
      });

      // 排序 & 赋值
      const processNodes = (nodes) => {
        return sortBy(nodes, 'priority').map((node) => {
          setData(node);
          if (node.children) {
            node.children = processNodes(node.children);
          }
          return node;
        });
      };
      return processNodes(result);
    };
    return buildFieldTree();
  }, [kpiFields, columns]);

  /**
   * 调用API获取数据
   */
  const getTableData = (req) => {
    setDrillSaveFields(req.drillSaveFields);
    const {
      compare,
      fiscalYear,
      expandFields,
      evaluationPeriod,
      dimension,
      currentSnapshot,
      targetSnapshot,
      currentPeriod = [],
      targetPeriod = [],
      aggregationPeriod,
      selectedKpis,
      selectedDrillData,
    } = req;
    const [startDate, endDate] = aggregationPeriod || [];
    setSelectedKpis(selectedKpis);
    setSelectedDrillData(selectedDrillData);
    const paramMap = {
      snapshot: {
        snapshot: [currentSnapshot, targetSnapshot],
        compare: 'snapshot',
      },
      period: {
        snapshot: [currentSnapshot],
        compare: 'period',
        timeFilter: [
          {
            startDate: currentPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: currentPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
          {
            startDate: targetPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: targetPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
        ],
      },
      plan: {
        snapshot: [currentSnapshot],
        compare: 'plan',
      },
      prev_year: {
        snapshot: [currentSnapshot],
        compare: 'prev_year',
      },
      prev_month: {
        snapshot: [currentSnapshot],
        compare: 'prev_month',
      },
    };
    const params = {
      websocketRoute: 'WebsocketOpenaiEvent',
      client_account: tralitSettings.account,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      fiscalYear,
      evaluationPeriod,
      reportType: dimension,
      expandFields: expandFields ? expandFields.map((fieldKey) => camelToSnake(fieldKey)) : null,
      ...paramMap[compare],
    };
    setTableLoading(true);
    APIList.getForecastTableData()
      .post(params)
      .then(async (res: any) => {
        const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME, res.bucketKey);
        const { fields, current, target, alert, computed } = response;
        const kpiFields = sortBy(
          fields
            .filter((field) => field.category === FIELD_CATEGORY.KPI_DATA)
            .map((field) => ({
              ...field,
              fieldKey: toCamelCase(field.fieldKey),
              parentKey: toCamelCase(field.parentKey),
            })),
          'priority'
        );
        setKpiFields(kpiFields);
        const drillFields = sortBy(
          fields.filter((item) => item.category === FIELD_CATEGORY.DRILL_DOWN && item.important >= FIELD_IMPORTANT.LEVEL0),
          'important'
        ).map((field) => ({
          ...field,
          fieldKey: toCamelCase(field.fieldKey),
          parentKey: toCamelCase(field.parentKey),
        }));
        setDrillFields(drillFields);
        setDataProvider({
          current,
          target,
          alert: (alert || []).map((item) => ({
            ...item,
            drillKey: toCamelCase(item.drillKey),
            kpiKey: toCamelCase(item.kpiKey),
          })),
          computed,
        });
        setTableLoading(false);
        setExpandedRowKeys(kpiFields.map((field) => field.fieldKey));
        // form
        if (!expandFields) {
          form.setFieldValue(
            'drillFields',
            sortBy(
              drillFields.filter((drillField) => drillField.important > FIELD_IMPORTANT.LEVEL0),
              'priority'
            ).map((drillField) => drillField.fieldKey)
          );
        }
      });
  };

  /**
   * 获取注册Component
   */
  useEffect(() => {
    if (componentDefinition) {
      setAllComponents({
        ...componentDefinition.StaticComponentDefinition,
        ...componentDefinition.CustomComponentDefinition,
      });
    }
  }, [componentDefinition]);

  useEffect(() => {
    setTableLoading(true);
  }, [tralitSettings.account]);

  /**
   * 事件监听：PAGE_INIT， FILTER_ITEM_CHANGED
   * 因为事件没有被消费，所以用globalEvent.data.aggregationPeriod判断事件执行条件
   */
  event$.useSubscription((globalEvent) => {
    if (!globalEvent || globalEvent.pageId !== PAGE_ID.FORECAST) {
      return;
    }
    if (globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT) {
      setFilter(globalEvent.data);
      getTableData({
        ...globalEvent.data,
        expandFields: globalEvent.data.drillSaveFields ? globalEvent.data.drillSaveFields.map((drillField) => drillField.fieldKey) : null,
      });
    }
    if (globalEvent.eventType === GLOBAL_EVENT.FILTER_ITEM_CHANGED) {
      setFilter(globalEvent.data);
      getTableData({
        ...globalEvent.data,
        expandFields: globalEvent.data.drillSaveFields ? globalEvent.data.drillSaveFields.map((drillField) => drillField.fieldKey) : null,
      });
    }
  });

  /**
   * Hover 十字高亮
   */
  useEffect(() => {
    if (instance.current) {
      const tbody = instance.current.nativeElement;
      tbody.onmouseleave = () => {
        setTimeout(() => {
          const cells = tbody.querySelectorAll('.tralit-table-cell');
          for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];
            cell.classList.remove('tralit-table-cell-row-hover');
          }
        }, 10);
      };
    }
  }, [instance.current]);

  return (
    <>
      <Card styles={{ body: { padding: 0 } }}>
        <Table
          ref={instance}
          size="small"
          className="small-header"
          bordered
          rowHoverable={false}
          rowKey="fieldKey"
          columns={columns}
          dataSource={dataSource}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (expandedRows) => {
              setExpandedRowKeys([...expandedRows]);
            },
          }}
          pagination={false}
          scroll={{
            x: 'max-content',
            y: selectorHeight4Table,
          }}
          loading={tableLoading}
        />
      </Card>
    </>
  );
};

export default ForecastTable;
